import { themeConWeb } from '@app/util/modernThemeConweb'
import { Box, Button, Card as MaterialCard, Snackbar, Typography } from '@material-ui/core'
import { Alert, Skeleton } from '@material-ui/lab'
import React, { isValidElement, useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'

import { api, FOODSBY_BASE_URL, subscriptionsCache } from '../../../api/api'
import { useSubscriptionDetails } from '../../../hooks/subscriptions'
import { addSubscriptionDetails } from '../../../redux/modules/subscriptions'
import { getSubscriptionState, SUBSCRIPTION_STATE } from '../../../util/subscriptions'
import PaymentMethod, { ButtonPaymentMethod } from '../../common/PaymentMethod'
import SubmitButton from '../../common/SubmitButton'
import PanelHeader from '../PanelHeader'
import DialogUnsubscribe from './DialogUnsubscribe'
import SubscriptionButton from './SubscriptionButton'
import SubscriptionMessage from './SubscriptionMessage'

const ERROR_MESSAGE_LOAD_SUBSCRIPTIONS =
  'There was a problem loading your Unlimited Delivery information. Please refresh the page and try again.'
const ERROR_MESSAGE_NO_CARD_SELECTED = 'Please select a payment method.'

const PanelSubscriptions = ({
  header: Header,
  component: Component = Card,
  message: Message,
  onSubscribeSuccess,
}) => {
  const dispatch = useDispatch()
  const { subscriptionEligible } = useSubscriptionDetails()

  // States
  const [hidden, setHidden] = useState(true)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [userSubscription, setUserSubscription] = useState(null)
  const [subscriptionState, setSubscriptionState] = useState(null)
  const [ccProfileId, setCcProfileId] = useState(null)
  const [showPaymentMethod, setShowPaymentMethod] = useState(false)
  const [showDialogUnsubscribe, setShowDialogUnsubscribe] = useState(false)
  const [errorLoadSubscription, setErrorLoadSubscription] = useState(null)
  const [errorSubscription, setErrorSubscription] = useState(null)
  const [errorCreditCard, setErrorCreditCard] = useState(null)
  const [creatingSubscription, setCreatingSubscription] = useState(false)
  const [updatingSubscription, setUpdatingSubscription] = useState(false)
  const ccFormRef = useRef()

  // Api
  const getUserSubscription = useCallback(async () => {
    setLoading(true)
    try {
      const sub = await api.get(`${FOODSBY_BASE_URL}/api/v1/credit-cards/subscriptions`, null, {
        cache: subscriptionsCache,
      })
      setUserSubscription(sub[0])
      dispatch(addSubscriptionDetails(sub[0] || null)) // Null if no previous subscription
    } catch (err) {
      setUserSubscription({})
      setErrorLoadSubscription(ERROR_MESSAGE_LOAD_SUBSCRIPTIONS)
    } finally {
      setLoading(false)
    }
  }, [dispatch])
  const cancelUserSubscription = async () => {
    try {
      setSubmitting(true)
      await api.post(
        `${FOODSBY_BASE_URL}/api/v1/credit-cards/subscriptions/${userSubscription?.subscriptionId}/cancel`,
      )
      subscriptionsCache.reset()
      getUserSubscription()
      setShowPaymentMethod(false)
      setCcProfileId(null)
    } catch (err) {
      setErrorSubscription(err.message)
    } finally {
      setSubmitting(false)
      setShowDialogUnsubscribe(false)
    }
  }

  const onCreateSubscription = () => {
    if (!showPaymentMethod) {
      setErrorSubscription(ERROR_MESSAGE_NO_CARD_SELECTED)
      return
    }
    if (ccProfileId) {
      createSubscription(ccProfileId)
    } else if (ccFormRef.current) {
      setCreatingSubscription(true)
      // Create and save a new payment method
      ccFormRef.current.handleSubmit()
    }
  }
  const onUpdateSubscription = () => {
    if (ccProfileId) {
      updateSubscription(ccProfileId)
    } else if (ccFormRef.current) {
      setUpdatingSubscription(true)
      // Create and save a new payment method
      ccFormRef.current.handleSubmit()
    }
  }

  const createSubscription = async creditCardProfileId => {
    try {
      setSubmitting(true)
      await api.post(`${FOODSBY_BASE_URL}/api/v1/salad/web/subscriptions`, {
        creditCardProfileId,
      })
      subscriptionsCache.reset()
      getUserSubscription()
      setShowPaymentMethod(false)
      if (onSubscribeSuccess) {
        onSubscribeSuccess()
      }
    } catch (err) {
      setErrorSubscription(err.message)
    } finally {
      setSubmitting(false)
    }
  }
  const updateSubscription = async creditCardProfileId => {
    try {
      await api.patch(
        `${FOODSBY_BASE_URL}/api/v1/salad/web/subscriptions/${userSubscription.subscriptionId}`,
        { creditCardProfileId },
      )
      subscriptionsCache.reset()
      getUserSubscription()
      setShowPaymentMethod(false)
    } catch (err) {
      setErrorCreditCard(err.message)
    }
  }

  const handleCreditCardSuccess = id => {
    setSubmitting(true)
    if (creatingSubscription) createSubscription(id)
    else if (updatingSubscription) updateSubscription(id)
    setSubmitting(false)
  }

  // Effects
  useEffect(() => {
    if (subscriptionEligible) {
      setHidden(false)
    } else {
      setHidden(true)
    }
  }, [subscriptionEligible])

  useEffect(() => {
    if (ccProfileId) {
      setErrorSubscription(null)
    }
  }, [ccProfileId])

  useEffect(() => {
    if (!loading && userSubscription === null) {
      subscriptionsCache.reset()
      getUserSubscription()
    }
  }, [getUserSubscription, loading, setLoading, userSubscription])

  useEffect(() => {
    const state = getSubscriptionState(userSubscription)
    setSubscriptionState(state)
  }, [subscriptionState, userSubscription])

  useEffect(() => {
    if (errorCreditCard || errorSubscription) {
      setSubmitting(false)
    }
  }, [errorCreditCard, errorSubscription])

  const isSubscriptionActive =
    subscriptionState === SUBSCRIPTION_STATE.EVERGREEN_ACTIVE ||
    subscriptionState === SUBSCRIPTION_STATE.TRIAL_ACTIVE

  return (
    <Component hidden={hidden}>
      {loading ? (
        <SkeletonLoading />
      ) : (
        <>
          {isValidElement(Header) ? (
            Header
          ) : (
            <PanelHeader
              headerRight={
                isSubscriptionActive &&
                !errorLoadSubscription && (
                  <Button
                    className="-button"
                    color="primary"
                    onClick={() => setShowDialogUnsubscribe(true)}
                    size="small"
                  >
                    Unsubscribe
                  </Button>
                )
              }
              title="Unlimited Delivery"
            />
          )}

          {!errorLoadSubscription && (
            <>
              {isValidElement(Message) ? (
                Message
              ) : (
                <SubscriptionMessage subscription={userSubscription} />
              )}
              <Box marginBottom={4}>
                <HeaderPaymentMethod>
                  <Typography className="-title" variant="subtitle2">
                    {isSubscriptionActive ? 'Applied' : 'Apply'} to
                  </Typography>
                  {isSubscriptionActive && !showPaymentMethod && (
                    <Button
                      className="-button"
                      color="primary"
                      onClick={() => setShowPaymentMethod(true)}
                      size="small"
                    >
                      Change
                    </Button>
                  )}
                </HeaderPaymentMethod>
                {isSubscriptionActive && !showPaymentMethod && (
                  <ButtonPaymentMethod disabled>
                    <Typography variant="body2">
                      &bull;&bull;&bull;&bull;&nbsp;&nbsp;{userSubscription?.lastFourCreditCard}
                    </Typography>
                  </ButtonPaymentMethod>
                )}
                {!isSubscriptionActive && !showPaymentMethod && (
                  <ButtonPaymentMethod onClick={() => setShowPaymentMethod(true)}>
                    <Typography color="primary" variant="body2">
                      Select payment method
                    </Typography>
                  </ButtonPaymentMethod>
                )}
                {showPaymentMethod && (
                  <PaymentMethod
                    ccFormRef={ccFormRef}
                    ccProfileId={ccProfileId}
                    onError={setErrorCreditCard}
                    onSuccess={handleCreditCardSuccess}
                    setCcProfileId={setCcProfileId}
                    setSubmitting={setSubmitting}
                  />
                )}
              </Box>
              {subscriptionState !== SUBSCRIPTION_STATE.TRIAL_ACTIVE &&
              subscriptionState !== SUBSCRIPTION_STATE.EVERGREEN_ACTIVE ? (
                <CreditCardSubmit alignLeft>
                  <SubscriptionButton
                    handleClick={() => onCreateSubscription()}
                    state={subscriptionState || SUBSCRIPTION_STATE.TRIAL_UNSUBSCRIBED}
                    submitting={submitting}
                  />
                  {errorCreditCard && (
                    <Typography className="-errorMessage" color="error" variant="body2">
                      {errorCreditCard}
                    </Typography>
                  )}
                </CreditCardSubmit>
              ) : (
                showPaymentMethod && (
                  <CreditCardSubmit>
                    <SubmitButton
                      color="primary"
                      onClick={onUpdateSubscription}
                      submitting={submitting}
                      variant="contained"
                    >
                      Use selected card
                    </SubmitButton>
                    {errorCreditCard && (
                      <Typography className="-errorMessage" color="error" variant="body2">
                        {errorCreditCard}
                      </Typography>
                    )}
                  </CreditCardSubmit>
                )
              )}
              {errorSubscription && (
                <Box marginTop={1}>
                  <Typography color="error" variant="body2">
                    {errorSubscription}
                  </Typography>
                </Box>
              )}
              <DialogUnsubscribe
                handleClose={() => setShowDialogUnsubscribe(false)}
                handleSubmit={cancelUserSubscription}
                submitting={submitting}
                subscription={userSubscription}
                visible={showDialogUnsubscribe}
              />
            </>
          )}
        </>
      )}
      <Snackbar open={!!errorLoadSubscription}>
        <Alert severity="error">{errorLoadSubscription}</Alert>
      </Snackbar>
    </Component>
  )
}

const SkeletonLoading = () => (
  <Box>
    <Box display="flex" flexDirection="row" justifyContent="space-between" marginBottom={3}>
      <Skeleton height={40} width={160} />
      <Skeleton height={40} width={160} />
    </Box>
    <Skeleton />
    <Skeleton width={200} />
    <Skeleton height={80} />
    <Skeleton height={60} width={140} />
  </Box>
)

const Card = styled(MaterialCard)`
  display: ${({ hidden }) => hidden && 'none'};
`

const CreditCardSubmit = styled.div`
  display: flex;
  justify-content: ${props => (props.alignLeft ? 'flex-start' : 'flex-end')};
  position: relative;

  & .-errorMessage {
    position: absolute;
    top: calc(${themeConWeb.baseUnit} * 11);
  }
`

const HeaderPaymentMethod = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: calc(${themeConWeb.baseUnit} * 2);

  & .-title {
    align-self: center;
    flex-grow: 1;
  }
`

export default PanelSubscriptions
